import Cookies from 'js-cookie';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logError } from '@zg-rentals/log-error';

import AppConfig from '../model/AppConfig';
import browserUtils from '../utils/browserUtils';
import { getServices } from '../utils/serviceUtils';
import sharedConfig from '../sharedConfig';

import type { PayloadAction } from '@reduxjs/toolkit';

import api from '../utils/api';

export const updateLocation = createAsyncThunk('app/updateLocation', async (url: string) => {
  window.router.updateLocation(url);
});

export const getIp = createAsyncThunk('app/getIp', async (skipClientProxy: boolean, { dispatch }) =>
  dispatch(api.users.ip(skipClientProxy)),
);

export const getAppConfig = createAsyncThunk('multibrand/getAppConfig', async (_, { dispatch }) => {
  const { errorMessage, success, appConfig } = await dispatch(
    api.multibrandSupport.appConfig({
      appName: 'zrm_web',
    }),
  );

  if (success) {
    return AppConfig(appConfig);
  } else {
    const error = new Error(errorMessage);

    logError({
      error,
      errorType: 'MultibrandGetAppConfigError',
      context: 'MultibrandActions.appConfig',
    });

    throw error;
  }
});

export const initialState = {
  brand: sharedConfig.defaultBrand,
  hostname: sharedConfig.brandHostnames[sharedConfig.defaultBrand],
  version: sharedConfig.version,
  cookieEnabledServices: getServices(),
  isWizardView: false,
  isFooterlessView: false,
  isDev: false,
  isMobile: false,
  isOnline: true,
  constellationScreenSize: 'xl',
  isScreenSizeComputed: false, // flag to keep track first screenSize compute
  appConfig: AppConfig({}),
  timeZone: browserUtils.timezone(),
  userAgent: '',
  prevPage: '',
  ip: '',
  fullReqIps: {},
  ipFromJava: {},
  isRunningInProd: false,
  csrfToken: '',
  canAccessDashboardRedesign: false,
  canAccessMaintenanceTracker: false,
  canAccessRentGuaranteeFromML: false,
  canAccessRentalProtectionFromML: false,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    calculateScreenSize: (state) => {
      const constellationScreenSize = browserUtils.screen.size();
      if (constellationScreenSize != null) {
        state.constellationScreenSize = constellationScreenSize;
        state.isScreenSizeComputed = true;
      }
    },

    updateFeatureFlag: (state, action: PayloadAction<{ name: string; value: string }>) => {
      // Keep store and cookies in sync
      // @ts-expect-error
      Cookies.set(action.payload.name, action.payload.value, { secure: true, sameSite: 'none' });

      state.cookieEnabledServices[action.payload.name] = action.payload.value;
    },

    updateAppConfig: (state, action: PayloadAction<{ name: string; value: string }>) => {
      // @ts-expect-error
      state.appConfig.config.features[action.payload.name] = action.payload.value;
    },

    updateIsWizardView: (state, action: PayloadAction<boolean>) => {
      state.isWizardView = action.payload;
    },

    updateCanAccessDashboardRedesign: (state, action: PayloadAction<boolean>) => {
      state.canAccessDashboardRedesign = action.payload;
    },

    updateCanAccessRentalProtectionFromML: (state, action: PayloadAction<boolean>) => {
      state.canAccessRentalProtectionFromML = action.payload;
    },

    updateCanAccessMaintenanceTracker: (state, action: PayloadAction<boolean>) => {
      state.canAccessMaintenanceTracker = action.payload;
    },

    updateIsOnline: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAppConfig.fulfilled, (state, action) => {
      state.appConfig = action.payload;
    });
  },
});

export const {
  calculateScreenSize,
  updateCanAccessDashboardRedesign,
  updateCanAccessMaintenanceTracker,
  updateCanAccessRentalProtectionFromML,
  updateIsOnline,
  updateIsWizardView,
  updateFeatureFlag,
  updateAppConfig,
} = appSlice.actions;

export default appSlice.reducer;
